import axios from 'axios';

// Single unified backend
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.nephrobot.aiatus.com';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
  },
});

// ============================================================================
// Token Management
// ============================================================================

const TOKEN_KEY = 'nephrobot_token';
const USER_KEY = 'nephrobot_user';

/**
 * Get stored token
 */
export const getToken = () => localStorage.getItem(TOKEN_KEY);

/**
 * Store token
 */
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);

/**
 * Remove token
 */
export const removeToken = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Get stored user
 */
export const getStoredUser = () => {
  const user = localStorage.getItem(USER_KEY);
  return user ? JSON.parse(user) : null;
};

/**
 * Store user
 */
export const setStoredUser = (user) => {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
};

// ============================================================================
// Axios Interceptor - Auto-add token to requests
// ============================================================================

api.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Token expired or invalid
      removeToken();
      // Optionally redirect to login
      // window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// ============================================================================
// Auth API Functions
// ============================================================================

/**
 * Register a new user
 */
export const register = async (registerData) => {
  const response = await api.post('/api/v1/auth/register', {
    first_name: registerData.firstName,
    last_name: registerData.lastName,
    email: registerData.email,
    password: registerData.password,
    institution: registerData.institution || null,
    profession: registerData.profession || null,
    country: registerData.country || null,
    city: registerData.city || null,
  });
  
  // Store token and user
  setToken(response.data.access_token);
  setStoredUser(response.data.user);
  
  return response.data;
};

/**
 * Login with username/email and password
 */
export const login = async (usernameOrEmail, password) => {
  const response = await api.post('/api/v1/auth/login', {
    username_or_email: usernameOrEmail,
    password
  });
  
  // Store token and user
  setToken(response.data.access_token);
  setStoredUser(response.data.user);
  
  return response.data;
};

/**
 * Logout - clear stored data
 */
export const logout = () => {
  removeToken();
};

/**
 * Get current user profile
 */
export const getCurrentUser = async () => {
  const response = await api.get('/api/v1/auth/me');
  return response.data;
};

/**
 * Verify if token is still valid
 */
export const verifyToken = async () => {
  try {
    const response = await api.post('/api/v1/auth/verify');
    return response.data.success;
  } catch {
    return false;
  }
};

/**
 * Check if user is logged in (has valid token)
 */
export const isLoggedIn = () => {
  return !!getToken();
};

// ============================================================================
// RAG API Functions
// ============================================================================

/**
 * Check backend health status
 */
export const checkHealth = async () => {
  const response = await api.get('/api/v1/health');
  return response.data;
};

/**
 * Query the system (RAG+LLM or LLM-only based on use_rag flag)
 */
export const queryRAG = async (queryRequest) => {
  const response = await api.post('/api/v1/query', queryRequest);
  return response.data;
};

/**
 * List all documents
 */
export const listDocuments = async () => {
  const response = await api.get('/api/v1/documents');
  return response.data;
};

/**
 * Delete all documents from vector store
 */
export const deleteAllDocuments = async () => {
  const response = await api.delete('/api/v1/documents');
  return response.data;
};

/**
 * Submit feedback for a response (simple JSON file)
 */
export const submitFeedback = async (feedbackData) => {
  const response = await api.post('/api/v1/feedback', feedbackData);
  return response.data;
};

// ============================================================================
// Chat API Functions (PostgreSQL)
// ============================================================================

/**
 * Get user's chats
 */
export const getUserChats = async (username) => {
  const response = await api.get(`/api/v1/chat/users/${username}/chats`);
  return response.data;
};

/**
 * Create a new chat
 */
export const createChat = async (username, title = 'New Chat') => {
  const response = await api.post(`/api/v1/chat/users/${username}/chats`, { title });
  return response.data;
};

/**
 * Update chat title
 */
export const updateChatTitle = async (chatId, title) => {
  const response = await api.put(`/api/v1/chat/chats/${chatId}`, { title });
  return response.data;
};

/**
 * Delete a chat
 */
export const deleteChatApi = async (chatId) => {
  const response = await api.delete(`/api/v1/chat/chats/${chatId}`);
  return response.data;
};

/**
 * Get chat messages
 */
export const getChatMessages = async (chatId) => {
  const response = await api.get(`/api/v1/chat/chats/${chatId}/messages`);
  return response.data;
};

/**
 * Save a message to chat
 */
export const saveMessage = async (chatId, message) => {
  const response = await api.post(`/api/v1/chat/chats/${chatId}/messages`, message);
  return response.data;
};

/**
 * Submit feedback for a message (PostgreSQL)
 */
export const submitMessageFeedback = async (messageId, rating, starRating = null, note = null) => {
  const response = await api.post(`/api/v1/chat/messages/${messageId}/feedback`, { 
    rating, 
    star_rating: starRating,
    note 
  });
  return response.data;
};

export default api;
